import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TEMPLATE_DEFAULTS,
  TEMPLATE_PLACEHOLDERS,
  TEMPLATE_SAMPLE,
  renderTemplate,
  unknownPlaceholders,
  type NotificationTemplateEvent,
} from '@fleet/shared';
import { integrationsApi, type NotificationTemplateView } from '../../api/integrations';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { formatInstant } from '../../ui/datetime';

/**
 * Die Texte der beiden zeitgesteuerten Nachrichten.
 *
 * Global und nicht je Fahrzeuggruppe: Der Wortlaut einer Erinnerung ist keine
 * Eigenschaft eines Autos.
 *
 * Die Vorschau benutzt **denselben** Renderer wie der Server
 * (`renderTemplate` aus `@fleet/shared`). Zwei Umsetzungen desselben
 * Platzhalters liefen auseinander, und dann widerspricht die Vorschau der
 * Nachricht, die ankommt — ein Fehler, den man erst bemerkt, wenn sich jemand
 * über den Text beschwert.
 */
export function MessageTemplates() {
  const query = useQuery({
    queryKey: ['admin-templates'],
    queryFn: () => integrationsApi.templates(),
  });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">Nachrichten bei Beginn und Ende</h3>
        <p className="max-w-3xl text-sm text-slate-500">
          Gehen als Direktnachricht an die buchende Person — nicht in einen Channel. Ausgelöst von
          der Uhr, nicht von einer Handlung: Ein Suchlauf prüft alle fünf Minuten, was fällig
          geworden ist.
        </p>
      </div>

      <AsyncBoundary query={query}>
        {(templates) => (
          <div className="space-y-4">
            {templates.map((template) => (
              <TemplateEditor key={template.event} template={template} />
            ))}
          </div>
        )}
      </AsyncBoundary>
    </div>
  );
}

function TemplateEditor({ template }: { template: NotificationTemplateView }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState(template.body);
  const [isEnabled, setIsEnabled] = useState(template.isEnabled);

  // Nach dem Speichern kommt der gespeicherte Stand zurück; der Entwurf zieht nach.
  useEffect(() => {
    setBody(template.body);
    setIsEnabled(template.isEnabled);
  }, [template.body, template.isEnabled]);

  const save = useMutation({
    mutationFn: () => integrationsApi.setTemplate(template.event, { isEnabled, body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-templates'] });
    },
  });

  /**
   * Geprüft wird schon beim Tippen, mit derselben Funktion wie am Endpunkt. Ein
   * vertipptes `{{zwek}}` soll hier auffallen und nicht dadurch, dass es Wochen
   * später wörtlich in einer Nachricht steht.
   */
  const unknown = unknownPlaceholders(body);
  const changed = body !== template.body || isEnabled !== template.isEnabled;
  const isDefaultText = body === TEMPLATE_DEFAULTS[template.event as NotificationTemplateEvent];

  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(event) => setIsEnabled(event.target.checked)}
            disabled={save.isPending}
            className="size-4 rounded border-slate-300"
          />
          <span className="font-medium">{template.label}</span>
        </label>

        {isEnabled ? (
          <Badge tone="emerald">wird verschickt</Badge>
        ) : (
          <Badge tone="neutral">abgeschaltet</Badge>
        )}
      </div>

      {/*
        Der Schalter statt „Text leeren": Ein leeres Feld lässt offen, ob jemand
        das Verschicken beenden wollte oder den Text nur noch nicht geschrieben
        hat. Deshalb weist das Schema einen leeren Text auch ausdrücklich ab.
      */}
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        rows={4}
        disabled={save.isPending}
        className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-500 disabled:bg-slate-50"
      />

      <div className="flex flex-wrap gap-1.5">
        {TEMPLATE_PLACEHOLDERS.map((placeholder) => (
          <button
            key={placeholder}
            type="button"
            disabled={save.isPending}
            onClick={() => setBody(`${body}{{${placeholder}}}`)}
            className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700 transition-colors hover:bg-slate-200"
            title={`Beispiel: ${TEMPLATE_SAMPLE[placeholder]}`}
          >
            {`{{${placeholder}}}`}
          </button>
        ))}
      </div>

      {/*
        Was bewusst fehlt, wird benannt. Sonst sucht jemand den Zweck in der
        Liste, findet ihn nicht und hält es für eine Lücke.
      */}
      <p className="text-xs text-slate-500">
        Zweck, Zieladresse und Mitfahrende gibt es hier absichtlich nicht: Die Vorlage ist ein
        global gepflegter Text und soll auch dann nichts verraten, wenn sie einmal in einem
        Channel landet.
      </p>

      {unknown.length > 0 && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900" role="alert">
          Unbekannte Platzhalter:{' '}
          <code className="font-mono">{unknown.map((name) => `{{${name}}}`).join(', ')}</code> — so
          geschrieben erscheinen sie wörtlich in der Nachricht.
        </p>
      )}

      <div>
        <p className="text-xs font-medium text-slate-700">Vorschau mit Beispielwerten</p>
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded-md bg-slate-50 px-3 py-2 text-sm">
          {renderTemplate(body, TEMPLATE_SAMPLE) || '(leer)'}
        </pre>
      </div>

      {save.isError && <ErrorNotice error={save.error} />}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          disabled={!changed || unknown.length > 0 || body.trim().length === 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
        </Button>
        {!isDefaultText && (
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() => setBody(TEMPLATE_DEFAULTS[template.event as NotificationTemplateEvent])}
          >
            Vorgabetext einsetzen
          </Button>
        )}
        {template.updatedAt && !changed && (
          <span className="text-xs text-slate-400">
            Geändert {formatInstant(template.updatedAt, 'medium')}
          </span>
        )}
      </div>
    </section>
  );
}
